import { useState } from "react";
import { X, ArrowRight, ArrowLeft, CheckCircle, AlertTriangle, Clock, Target, TrendingUp, Info } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ReadinessAssessment = ({ onClose }) => {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [formData, setFormData] = useState({
    industry: "",
    target_certification: "",
    has_documented_processes: false,
    has_quality_policy: false,
    has_internal_audits: false,
    has_management_review: false,
    has_corrective_action_process: false,
    has_training_program: false,
    has_document_control: false,
    has_risk_assessment: false,
    employees_trained_percentage: 0,
    years_in_operation: 0
  });

  const industries = [
    "Food Manufacturing",
    "Farms & Primary Production",
    "Packing & Distribution",
    "Hospitality",
    "Other Manufacturing",
    "Service Organization",
    "Government",
    "Healthcare"
  ];

  const certifications = [
    "ISO 9001 - Quality Management",
    "ISO 22000 - Food Safety",
    "FSSC 22000 - Food Safety Certification",
    "HACCP - Food Safety",
    "GlobalG.A.P. - Agriculture",
    "ISO 27001 - Information Security",
    "ISO 31000 - Risk Management",
    "ISO 45001 - Health & Safety"
  ];

  const assessmentQuestions = [
    { key: "has_documented_processes", question: "Do you have documented operational processes and procedures?", tooltip: "Written procedures for your key business activities" },
    { key: "has_quality_policy", question: "Do you have a quality/food safety policy statement?", tooltip: "A formal policy approved by top management" },
    { key: "has_document_control", question: "Do you have a document control system?", tooltip: "A system for managing versions and approval of documents" },
    { key: "has_risk_assessment", question: "Do you conduct systematic risk assessments?", tooltip: "Formal process for identifying and evaluating risks" },
    { key: "has_internal_audits", question: "Do you conduct internal audits?", tooltip: "Regular checks of your system against requirements" },
    { key: "has_corrective_action_process", question: "Do you have a corrective action process?", tooltip: "System for addressing problems and preventing recurrence" },
    { key: "has_management_review", question: "Does top management review system performance?", tooltip: "Regular management meetings to review the system" },
    { key: "has_training_program", question: "Do you have a staff training program?", tooltip: "Formal training for employees on procedures and requirements" }
  ];

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/tools/readiness-assessment`, formData);
      setResult(res.data);
      
      // Save to session
      const sessionId = localStorage.getItem("chatbot_session_id");
      if (sessionId) {
        await axios.post(`${API}/tools/session/save`, {
          session_id: sessionId,
          tool_name: "readiness_assessment",
          result: res.data
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getReadinessColor = (level) => {
    switch (level) {
      case "near_ready": return "bg-green-500";
      case "developing": return "bg-amber-500";
      case "beginner": return "bg-red-500";
      default: return "bg-slate-500";
    }
  };

  const getReadinessLabel = (level) => {
    switch (level) {
      case "near_ready": return "Near Ready";
      case "developing": return "Developing";
      case "beginner": return "Beginner";
      default: return level;
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">What industry are you in?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select your primary industry to tailor the assessment.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {industries.map((ind) => (
                <button
                  key={ind}
                  onClick={() => { setFormData({ ...formData, industry: ind }); setStep(2); }}
                  className={`p-4 rounded-xl border-2 text-left transition-all hover:border-brand ${
                    formData.industry === ind ? 'border-brand bg-brand/5' : 'border-slate-200'
                  }`}
                >
                  <span className="font-medium text-sm">{ind}</span>
                </button>
              ))}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Which certification are you targeting?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select the certification you want to assess your readiness for.
              </p>
            </div>
            <div className="space-y-2">
              {certifications.map((cert) => (
                <button
                  key={cert}
                  onClick={() => { setFormData({ ...formData, target_certification: cert }); setStep(3); }}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all hover:border-brand ${
                    formData.target_certification === cert ? 'border-brand bg-brand/5' : 'border-slate-200'
                  }`}
                >
                  <span className="font-medium">{cert}</span>
                </button>
              ))}
            </div>
            <div className="flex justify-start pt-4">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Current System Assessment</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Answer honestly - this helps us provide accurate guidance. There are no wrong answers.
              </p>
            </div>
            <div className="space-y-4">
              {assessmentQuestions.map((q) => (
                <div 
                  key={q.key}
                  className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                    formData[q.key] ? 'border-green-500 bg-green-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setFormData({ ...formData, [q.key]: !formData[q.key] })}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      formData[q.key] ? 'bg-green-500 border-green-500' : 'border-slate-300'
                    }`}>
                      {formData[q.key] && <CheckCircle className="h-4 w-4 text-white" />}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{q.question}</p>
                      <p className="text-xs text-muted-foreground mt-1">{q.tooltip}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              <Button 
                onClick={handleSubmit} 
                className="bg-brand hover:bg-brand-dark"
                disabled={isLoading}
              >
                {isLoading ? "Analyzing..." : "Get Assessment"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (result) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-primary">Your Readiness Assessment</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Readiness Score */}
        <div className="bg-slate-50 rounded-2xl p-6 mb-6 text-center">
          <div className="relative w-32 h-32 mx-auto mb-4">
            <svg className="w-32 h-32 transform -rotate-90">
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="#e2e8f0"
                strokeWidth="12"
                fill="none"
              />
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="12"
                fill="none"
                strokeDasharray={`${(result.score_percentage / 100) * 352} 352`}
                className={`${
                  result.readiness_level === "near_ready" ? "text-green-500" :
                  result.readiness_level === "developing" ? "text-amber-500" : "text-red-500"
                }`}
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-3xl font-bold">{result.score_percentage}%</span>
            </div>
          </div>
          <Badge className={`${getReadinessColor(result.readiness_level)} text-white px-4 py-1`}>
            {getReadinessLabel(result.readiness_level)}
          </Badge>
          <p className="mt-3 text-sm text-muted-foreground">
            Target: {result.target_certification}
          </p>
        </div>

        {/* Timeline */}
        <div className="bg-brand/5 border border-brand/20 rounded-xl p-4 mb-6 flex items-center gap-3">
          <Clock className="h-5 w-5 text-brand flex-shrink-0" />
          <div>
            <p className="font-medium text-sm">Estimated Timeline</p>
            <p className="text-sm text-muted-foreground">{result.estimated_timeline}</p>
          </div>
        </div>

        {/* Strengths & Gaps */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-green-50 rounded-xl p-4">
            <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" /> Strengths
            </h3>
            <ul className="space-y-2">
              {result.strengths.length > 0 ? result.strengths.map((s, i) => (
                <li key={i} className="text-sm text-green-700 flex items-start gap-2">
                  <span className="text-green-500">•</span> {s}
                </li>
              )) : (
                <li className="text-sm text-green-600 italic">Building from the ground up</li>
              )}
            </ul>
          </div>
          <div className="bg-amber-50 rounded-xl p-4">
            <h3 className="font-semibold text-amber-700 mb-3 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Areas to Develop
            </h3>
            <ul className="space-y-2">
              {result.gaps.map((g, i) => (
                <li key={i} className="text-sm text-amber-700 flex items-start gap-2">
                  <span className="text-amber-500">•</span> {g}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-slate-50 rounded-xl p-4 mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Target className="h-4 w-4 text-brand" /> Recommendations
          </h3>
          <ul className="space-y-2">
            {result.recommendations.map((rec, i) => (
              <li key={i} className="text-sm flex items-start gap-2">
                <span className="font-bold text-brand">{i + 1}.</span> {rec}
              </li>
            ))}
          </ul>
        </div>

        {/* Disclaimer */}
        <div className="bg-slate-100 rounded-lg p-3 mb-6">
          <p className="text-xs text-slate-500 flex items-start gap-2">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            {result.disclaimer}
          </p>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={() => { setResult(null); setStep(1); }} className="flex-1">
            Start Over
          </Button>
          <Button onClick={onClose} className="flex-1 bg-brand hover:bg-brand-dark">
            Request Expert Review
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-primary">Compliance Readiness Assessment</h2>
          <p className="text-sm text-muted-foreground">Step {step} of 3</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-slate-100 rounded-full h-2 mb-8">
        <div 
          className="bg-brand h-2 rounded-full transition-all duration-300"
          style={{ width: `${(step / 3) * 100}%` }}
        />
      </div>

      {renderStep()}
    </div>
  );
};

export default ReadinessAssessment;
